import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {t} from 'i18next';
import {Box, ToggleButton, ToggleButtonGroup, Grid} from '@mui/material';
import {GridSortModel} from '@mui/x-data-grid';

import {RootState} from '../../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {
  SelectedClientState,
  setClientData,
  setHideClientDropdown,
  setSkipClientSelectionModal,
} from '../../../../redux/slices/selectedClientSlice';
import {setWelcomeMultiClientPageState} from '../../../../redux/slices/pageViewStateSlice';
import {resetPermissionsByProject} from '../../../../redux/slices/projectPermissionsSlice';

import {getClientsList} from '../../../../api/UserManagementService';
import {getMultiClientColumns} from '../../../../utils/welcomeMultiClientUtils';

import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import AurSearchBox from '../../../common/aur-search-box/AurSearchBox';
import AurCardGrid from '../../../common/aur-card-grid/AurCardGrid';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  failedToLoadStatusCodes,
  INVOICE_REPORT_GRID_ERROR_COMPONENT,
  MIN_RECORDS_FOR_CLIENT_GRID,
  MIN_SEARCH_CHAR,
  MULTI_CLIENT_VIEW_TOGGLE,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../../constants/constants';
import AurDataGrid from '../../../common/aur-data-grid/AurDataGrid';
import AurNoResults from '../../../common/aur-no-results/AurNoResults';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import ClientCard from './ClientCard';
import {scrollToGridView} from '../../../../utils/utils';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../../constants/analyticsConstants';

export interface ClientData {
  clientId: number;
  name: string;
  consilioClientId: number;
  lastProjectCreatedTime: string;
  totalActiveBranches: number;
  totalActiveProjects: number;
  totalBranches: number;
  clientCode: string | number;
}

const ClientViewTab = () => {
  const [data, setData] = useState<ClientData[]>([]); // Data to display in DataGrid
  const [totalClients, setTotalClients] = useState<number>(0);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'name', sort: 'asc'},
  ]); // Sorting state
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const hasInitializedRef = useRef(false);
  const {trackGAEvent} = useAnalytics();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [currentPageCard, setCurrentPageCard] = useState<number>(1);
  const [itemsPerPageCard, setItemsPerPageCard] = useState<number>(12);
  const [isGetClientsListLoading, setIsGetClientsListLoading] = useState(false);
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) +
      INVOICE_REPORT_GRID_ERROR_COMPONENT
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clientViewMode = useSelector(
    (state: RootState) => state.pageViewState.welcomeMultiClient
  );
  const setViewMode = (viewMode: string) => {
    setButtonValue(viewMode);
    dispatch(
      setWelcomeMultiClientPageState(
        viewMode == MULTI_CLIENT_VIEW_TOGGLE.LIST_VIEW ? 'table' : 'tile'
      )
    );
  };
  const [buttonValue, setButtonValue] = useState<string | null>(
    clientViewMode == 'table'
      ? MULTI_CLIENT_VIEW_TOGGLE.LIST_VIEW
      : MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW
  );

  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });

  const fetchClients = async (
    pageNumber: number,
    pageSize: number,
    viewType: string | null = buttonValue
  ) => {
    try {
      setIsGetClientsListLoading(true);
      setIsFailedToLoadContent(false);
      const paramString = buildParamString(pageNumber, pageSize, viewType);
      if (searchKeyword) {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.SEARCH,
          click: {
            name: t('gaTracking.welcomeScreen.runSearch'),
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.BODY,
            action: t('gaTracking.welcomeScreen.ranSearch'),
          },
        });
      }
      const result = await getClientsList(paramString);

      const total = result.data.meta.pagination.totalItems;
      const clientsList = result.data.data;
      setData(
        clientsList.map((item: ClientData) => ({
          id: item.clientId,
          ...item,
          lastProjectCreatedTime: item.lastProjectCreatedTime,
        }))
      );
      setTotalClients(total);
      if (searchKeyword.trim() === '') {
        if (total === 1) {
          dispatch(resetPermissionsByProject());
          dispatch(setSkipClientSelectionModal(true));
          dispatch(setClientData(clientsList?.[0] as SelectedClientState));
          navigate('/welcome-single-client');
        } else if (total === 0) {
          dispatch(setHideClientDropdown(true));
          dispatch(setSkipClientSelectionModal(true));
          navigate('/welcome-no-client');
        }
      }

      // switch to grid view if less than 12 clients
      if (searchKeyword.trim() === '' && !hasInitializedRef.current) {
        hasInitializedRef.current = true;
        if (
          total < MIN_RECORDS_FOR_CLIENT_GRID &&
          buttonValue !== MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW
        ) {
          setViewMode(MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW);
        }
      }

      if (buttonValue === MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW) {
        setTimeout(() => {
          scrollToGridView('#client-data-container');
        }, 300);
      }
    } catch (error: any) {
      if (failedToLoadStatusCodes.includes(error.status)) {
        setIsFailedToLoadContent(true);
      }
      console.error('Error fetching clients:', error);
    } finally {
      setIsGetClientsListLoading(false);
      setIsInitialLoadComplete(true);
    }
  };

  const buildParamString = (
    pageNumber: number,
    pageSize: number,
    viewType: string | null
  ) => {
    const isListView = viewType === MULTI_CLIENT_VIEW_TOGGLE.LIST_VIEW;
    const params = new URLSearchParams();
    params.set('SearchTerm', searchKeyword);
    if (sortModel[0]?.field) params.set('SortColumn', sortModel[0]?.field);
    if (sortModel[0]?.sort) params.set('SortOrder', sortModel[0]?.sort);
    params.set(
      'PageNumber',
      (isListView ? pageNumber : currentPageCard).toString()
    );
    params.set(
      'PageSize',
      (isListView ? pageSize : itemsPerPageCard).toString()
    );

    return params.toString();
  };

  useEffect(() => {
    fetchClients(currentPage, itemsPerPage);
  }, [
    currentPage,
    searchKeyword,
    sortModel,
    itemsPerPage,
    currentPageCard,
    itemsPerPageCard,
  ]);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  // Handler for toggle button changes
  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    selectedButton: string | null
  ) => {
    if (selectedButton !== null) {
      setViewMode(selectedButton);

      if (selectedButton === MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW) {
        fetchClients(currentPageCard, itemsPerPageCard, selectedButton);
      } else {
        fetchClients(currentPage, itemsPerPage, selectedButton);
      }
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name:
            selectedButton === MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW
              ? t('gaTracking.welcomeScreen.gridView')
              : t('gaTracking.welcomeScreen.listView'),
          type: GA_CLICK_TYPES.ICON,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.welcomeScreen.clientViewChange'),
        },
      });
    }
  };

  const handleArrowClick = (data: SelectedClientState) => {
    dispatch(resetPermissionsByProject());
    dispatch(setClientData(data));
    navigate('/welcome-single-client');
  };

  const handleCardClick = (data: any) => {
    dispatch(resetPermissionsByProject());
    dispatch(setClientData(data as SelectedClientState));
    navigate('/welcome-single-client');
  };

  const multiClientColumnsName = [
    ...getMultiClientColumns(),
    {
      field: 'arrow',
      headerName: '',
      minWidth: 24,
      flex: 0.4,
      sortable: false,
      isBorderNotRequired: true,
      headerClassName: 'no-separator',
      renderCell: (params: {row: any}) => (
        <img
          style={{cursor: 'pointer'}}
          src="/assests/images/chev_right.svg"
          onClick={() => handleArrowClick(params?.row)}
          alt="right-icon"
        />
      ),
    },
  ];

  const renderDataView = () => {
    // No data
    if (!isInitialLoadComplete) {
      return null;
    }
    if (data?.length <= 0) {
      return <AurNoResults />;
    }

    // Grid View
    if (buttonValue === MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW) {
      return (
        <AurCardGrid<ClientData>
          rows={data}
          pageSizeOptions={[12, 24, 36]}
          entityType={t('welcomeScreenMultiClient.users')}
          showPagination={true}
          totalRowCount={totalClients}
          CardComponent={ClientCard}
          onClickHandler={handleCardClick}
          currentPage={currentPageCard}
          setCurrentPage={setCurrentPageCard}
          itemsPerPage={itemsPerPageCard}
          setItemsPerPage={setItemsPerPageCard}
        />
      );
    }

    // List View
    if (buttonValue === MULTI_CLIENT_VIEW_TOGGLE.LIST_VIEW) {
      const ErrorView = isFailedToLoadContent
        ? AurDataGridErrorOverlay
        : undefined;

      return (
        <AurDataGrid
          rows={data}
          columns={multiClientColumnsName}
          visibleToolTip={true}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
          checkBoxVisible={false}
          entityType={t('welcomeScreenMultiClient.users')}
          className="multiClientDataGrid"
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalRowCount={totalClients}
          sortModel={sortModel}
          onRowClick={(params) => handleArrowClick(params.row)}
          setSortModel={onSetSortModel}
          onRefresh={() => fetchClients(currentPage, itemsPerPage)}
          containerHeight={size}
          noRowsOverlay={
            isGetClientsListLoading ? AurDataGridLoadingOverlay : ErrorView
          }
          enableRowClickOnEnterPress
          defaultHeaderCellTabIndex={0}
        />
      );
    }

    // Default
    return <AurNoResults />;
  };

  return (
    <>
      <Box className="client-view_tab-header">
        <Grid container direction="row" justifyContent="space-between">
          <Grid>
            <AurSearchBox
              label={t('welcomeScreenMultiClient.searchByClientName')}
              minCharForSearch={MIN_SEARCH_CHAR}
              value={searchKeyword}
              onSearch={(keyword: string) => {
                setSearchKeyword(keyword);
                setCurrentPage(1);
                setCurrentPageCard(1);
              }}
              className="aur-autofill-style"
            />
          </Grid>
          <Grid>
            <ToggleButtonGroup
              className="toggle-group"
              value={buttonValue}
              exclusive
              onChange={handleToggleChange}
            >
              <ToggleButton
                value={MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW}
                aria-label={t('aria.cardView')}
                className="left-toggle"
                data-testid="grid-view"
              >
                <AurIcon icon={IconNames.GridView} />
              </ToggleButton>
              <ToggleButton
                value={MULTI_CLIENT_VIEW_TOGGLE.LIST_VIEW}
                aria-label={t('aria.listView')}
                className="right-toggle"
                data-testid="list-view"
              >
                <AurIcon icon={IconNames.ListView} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Box>

      <Box
        id="client-data-container"
        className={`client-view_tab-body content ${buttonValue === MULTI_CLIENT_VIEW_TOGGLE.GRID_VIEW ? 'listview' : ''}`}
      >
        {renderDataView()}
      </Box>

      {<AurLoadingIndicator overlay={true} isOpen={isGetClientsListLoading} />}
    </>
  );
};

export default ClientViewTab;
